import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { OAuthEvent, OAuthService } from "angular-oauth2-oidc";
import { JwksValidationHandler } from "angular-oauth2-oidc-jwks";
import { AuthBaseService, LoggerService } from "core-kit";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { authConfig } from "../../auth.config";
import { AccountService } from "./account.service";
import { AppConfigService } from "./app-config.service";

@Injectable()
export class AuthService extends AuthBaseService {
  private silentRefreshSubscription: Subscription | null = null;

  constructor(
    protected oAuthService: OAuthService,
    protected accountSvc: AccountService,
    private configSvc: AppConfigService,
    private router: Router,
    private logger: LoggerService
  ) {
    super(oAuthService, accountSvc);
  }

  isImpersonated(): boolean {
    return this.accountSvc.isLoggedIn && this.accountSvc.user.actsub;
  }

  protected postLogoutActions(): void {
    localStorage.setItem("actAsUserId", "");
  }

  async configureOidc(): Promise<void> {
    const authConf: IAuthConfig = this.configSvc.appData.authConfig;
    this.oAuthService.configure(authConfig(authConf));

    const urlParams = new URLSearchParams(window.location.search);
    const actAsUserId = urlParams.get("actAsUserId") || sessionStorage.getItem("actAsUserId");
    const posSystemId = urlParams.get("posSystemId") || sessionStorage.getItem("posSystemId");
    const posCompanyId = urlParams.get("merchant_id") || sessionStorage.getItem("posCompanyId");

    if (actAsUserId) {
      this.oAuthService.setStorage(sessionStorage);

      if (urlParams.get("actAsUserId")) {
        sessionStorage.clear();
      }
      sessionStorage.setItem("actAsUserId", actAsUserId);
    } else {
      this.oAuthService.setStorage(localStorage);
    }

    this.oAuthService.tokenValidationHandler = new JwksValidationHandler();
    const culture = "en-US";
    this.oAuthService.customQueryParams = {
      ui_locales: culture,
      actAsUserId: actAsUserId
    };

    if (posSystemId) {
      this.oAuthService.customQueryParams["posSystemId"] = posSystemId;
      sessionStorage.setItem("posSystemId", posSystemId);
    }

    if (posCompanyId) {
      this.oAuthService.customQueryParams["posCompanyId"] = posCompanyId;
      sessionStorage.setItem("posCompanyId", posCompanyId);
    }

    try {
      await this.oAuthService.loadDiscoveryDocumentAndTryLogin();
      const user = this.accountSvc.user;

      this.accountSvc.refreshUser(user);

      if (this.oAuthService.hasValidAccessToken()) {
        this.oAuthService.setupAutomaticSilentRefresh();
        this.setupSilentRefreshErrorHandling();
      }

      const redirectPath = localStorage.getItem("redirectPath");
      if (redirectPath && this.accountSvc.isLoggedIn) {
        this.router.navigateByUrl(redirectPath);
        localStorage.removeItem("redirectPath");
      }
    } catch (error) {
      this.logger.error("OIDC configuration error", "Auth", error);
    }
  }

  /**
   * Subscribe to OAuth events to monitor silent refresh behavior.
   * Logs all token-related events for debugging and handles refresh failures.
   */
  private setupSilentRefreshErrorHandling(): void {
    this.silentRefreshSubscription?.unsubscribe();

    this.silentRefreshSubscription = this.oAuthService.events
      .pipe(
        filter(
          (e: OAuthEvent) =>
            e.type === "token_received" ||
            e.type === "token_refreshed" ||
            e.type === "token_expires" ||
            e.type === "silent_refresh_error" ||
            e.type === "token_refresh_error" ||
            e.type === "session_terminated" ||
            e.type === "token_validation_error"
        )
      )
      .subscribe((event: OAuthEvent) => {
        const expiresAt = this.oAuthService.getAccessTokenExpiration();
        const expiresIn = expiresAt ? Math.round((expiresAt - Date.now()) / 1000) : "N/A";

        this.logger.info(`Event: ${event.type}, Token expires in: ${expiresIn}s`, "Auth");

        if (event.type === "silent_refresh_error" || event.type === "token_refresh_error") {
          this.logger.error("Silent refresh failed", "Auth", event);
        }

        if (event.type === "session_terminated") {
          this.logger.warn("Session terminated, redirecting to login...", "Auth");
          this.oAuthService.initImplicitFlow();
        }
      });
  }
}
